// src/Pages/ProviderProfile/OwnerNextStepCard.jsx
//
// One "what to do next" card at the top of the owner's own business page,
// on mobile AND desktop. Exists because on mobile every owner tool — drafts,
// integrations — sits behind the ☰ drawer, below voice cloning, video and
// audio. In 60 days only 4 owners ever started a social connection.
//
// Blocked businesses render nothing here: BusinessStatusBanner already asks
// them for more detail, and connecting is pointless until they have drafts.

import { useNavigate } from 'react-router-dom';
import { Paper, Box, Typography, Button, Stack } from '@mui/material';
import {
  Facebook as FacebookIcon,
  RateReview as DraftsIcon,
} from '@mui/icons-material';
import { useGetDraftsQuery } from '../../redux/thunks/drafts/getdrafts';
import { useGetSocialAccountsQuery } from '../../redux/thunks/ai/getsocialaccounts';

export default function OwnerNextStepCard({ businessId, businessName, businessQuality }) {
  const navigate = useNavigate();
  const skip = !businessId;

  // No polling here — this is a one-glance card, not a live view.
  const { data: pendingData } = useGetDraftsQuery(
    { businessId, status: 'pending_approval' }, { skip });
  const { data: parkedData } = useGetDraftsQuery(
    { businessId, status: 'approved_pending_connection' }, { skip });
  const { data: social, isLoading: socialLoading } = useGetSocialAccountsQuery(undefined, { skip });

  console.log('[OwnerNextStepCard]', {
    businessId, skip, socialLoading,
    tier: businessQuality?.tier,
    accounts: (social?.accounts || []).map(a => ({ platform: a.platform, isHealthy: a.isHealthy })),
    pending: pendingData?.total, parked: parkedData?.total,
  });
  if (skip || socialLoading) return null;
  if (businessQuality?.tier === 'blocked') return null;

  const connected = (social?.accounts || []).some(a => a.isHealthy);
  const pending   = pendingData?.total || 0;
  const parked    = parkedData?.total || 0;
  const navState  = { businessName, returnPath: window.location.pathname };

  const goConnect = () => navigate(`/business/${businessId}/social/accounts`, { state: navState });
  const goDrafts  = () => navigate(`/business/${businessId}/drafts`, { state: { businessName } });

  if (!connected) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
          {parked > 0
            ? `${parked} approved post${parked === 1 ? ' is' : 's are'} waiting to go live`
            : 'Next step: connect Facebook or Instagram'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          Connect once and Revfer posts what you approve, at the best time for each platform.
          Nothing goes out without your approval, and you can disconnect anytime.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" startIcon={<FacebookIcon />} onClick={goConnect}
            fullWidth sx={{ textTransform: 'none', fontWeight: 700 }}>
            Connect Facebook & Instagram
          </Button>
          {pending > 0 && (
            <Button variant="outlined" startIcon={<DraftsIcon />} onClick={goDrafts}
              fullWidth sx={{ textTransform: 'none', fontWeight: 600 }}>
              Review {pending} draft{pending === 1 ? '' : 's'}
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Instagram connects through your Facebook Page if it is a business account linked to that Page.
        </Typography>
      </Paper>
    );
  }

  if (pending > 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, border: '2px solid', borderColor: 'warning.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 180 }}>
            {pending} draft{pending === 1 ? ' is' : 's are'} ready for you
          </Typography>
          <Button variant="contained" color="warning" startIcon={<DraftsIcon />} onClick={goDrafts}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Review now
          </Button>
        </Box>
      </Paper>
    );
  }

  return null;
}